import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivationEnd, ResolveEnd, Router } from '@angular/router';

import { OfferMapping } from '@cds/query-objects';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { overrideActiveItemByOrder, sortByKey } from '@frontend/sports/common/core/utils/list';
import { CustomerDnaProfileStorageService } from '@frontend/sports/customer-dna/data-access';
import { FavouriteChangeType, FavouritesService } from '@frontend/sports/favourites/core/feature';
import { filterSports, filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { SlotLayoutService } from '@frontend/sports/layout/feature';
import { LiveUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { Subject, filter, take, takeUntil } from 'rxjs';

import { LiveFavouriteFixturesService } from '../favourites/live-favourites-fixtures.service';
import { TrackNavArea } from '../navigation/navigation-tracking.model';
import { SportNavigationComponent } from '../navigation/sport-navigation/sport-navigation.component';
import { PopupManager } from '../popup/popup-manager.service';
import { TabBarItem } from '../tab-bar/tab-bar.models';
import { ILiveDispatcher, LiveDispatcherEvents } from './live-dispatcher-events';
import { BaseLiveSportsModel, LiveEventListService } from './live-event-list.service';

@Injectable()
export class LiveSportNavigationService implements OnDestroy {
    private readonly layoutService = inject(SlotLayoutService);
    private readonly router = inject(Router);
    private readonly liveService = inject(LiveEventListService);
    private readonly dispatcher = inject<ILiveDispatcher>(DispatcherService);
    private readonly liveFavouriteFixturesService = inject(LiveFavouriteFixturesService);
    private readonly popupManager = inject(PopupManager);
    private readonly favouritesConfig = inject(FavouritesConfig);
    private readonly favouritesService = inject(FavouritesService);
    private readonly customerDnaProfileStorageService = inject(CustomerDnaProfileStorageService);

    sports?: TabBarItem[] = [];
    customSortedSportsMenu: TabBarItem[] | null = [];

    private readonly detached$ = new Subject<void>();

    init(): void {
        if (!this.liveService.skipSportNavigation) {
            this.router.events
                .pipe(
                    filterSports(),
                    filter((e) => e instanceof ResolveEnd),
                    take(1),
                )
                .subscribe(() => {
                    this.layoutService.register(SportNavigationComponent, {
                        attributes: {
                            model: this.customSortedSportsMenu?.length ? this.customSortedSportsMenu : this.sports,
                            tracking: TrackNavArea.NavLive,
                        },
                        order: 20,
                    });
                });
            this.router.events
                .pipe(
                    filterSports(),
                    filter((e) => {
                        const isAzMenuOpened = this.popupManager.isPopupOpen('sports');
                        if (!isAzMenuOpened && (e as any) instanceof ActivationEnd) {
                            const event = e as ActivationEnd;

                            return !!event.snapshot.data.model?.sports && event.snapshot.data.tag !== RouteTag.LiveLeague;
                        }

                        return false;
                    }),
                    takeUntil(this.detached$),
                )
                .subscribe((e) => {
                    this.setSportNavigation((e as ActivationEnd).snapshot.data.model);
                });
            this.dispatcher
                .on(LiveDispatcherEvents.ChildViewDataUpdate)
                .pipe(filterSportsEmitLast(), takeUntil(this.detached$))
                .subscribe((model) => {
                    this.setSportNavigation(model);
                });
        }
        if (this.favouritesConfig.isLiveFavouritesEnabled) {
            this.liveFavouriteFixturesService.change.pipe(filterSportsEmitLast(), takeUntil(this.detached$)).subscribe(() => this.setCount());
            this.favouritesService.change.pipe(filterSportsEmitLast(), takeUntil(this.detached$)).subscribe((favourites) => {
                const favouriteTab = this.sports?.find((tab) => tab.id === LiveUrlParam.Favourites);
                if (favouriteTab?.active) {
                    return;
                }
                if (favourites.changeType === FavouriteChangeType.Remove) {
                    this.liveFavouriteFixturesService.updateFixtureList();
                } else {
                    this.liveFavouriteFixturesService.refresh(OfferMapping.None);
                }
            });
        }
    }

    detach(): void {
        this.detached$.next();
        if (!this.liveService.skipSportNavigation) {
            this.layoutService.remove(SportNavigationComponent);
        }
    }

    ngOnDestroy() {
        this.detach();
    }

    private setSportNavigation(model: BaseLiveSportsModel): void {
        this.sports!.length = 0;

        this.liveService.getSportTabs(model.sports, model.videoEventCount, model.tab).forEach((tab) => this.sports!.push(tab));
        this.setCount();

        this.customSortedSportsMenu = this.setCustomOrderForSports(this.sports);
    }

    private setCount(): void {
        if (this.sports) {
            const favouriteTab = this.sports.find((tab) => tab.id === LiveUrlParam.Favourites);
            if (favouriteTab) {
                favouriteTab.count = this.liveFavouriteFixturesService.liveFixtures.length;

                if (favouriteTab.count) {
                    favouriteTab.isHidden = false;
                } else {
                    favouriteTab.isHidden = true;
                }
            }
        }
    }

    private setCustomOrderForSports(sports: TabBarItem[] | undefined): TabBarItem[] | null {
        const topSports = this.customerDnaProfileStorageService.customerDnaTopSports;

        if (!!topSports?.length && sports) {
            const sortedItems = sortByKey(topSports, sports, 'id');
            return overrideActiveItemByOrder(sortedItems);
        } else {
            return null;
        }
    }
}
